import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef
} from "@angular/core";

import {
  OverlayContext,
  OverlayRef,
  OverlayService
} from "@lantern/app/core/utils/overlays/";
import {
  AnchorPositionStrategy,
  PositionOption
} from "@lantern/app/core/utils/overlays/position-strategies";

@Directive({
  selector: "[taHoverable]"
})
export class HoverableDirective implements OnDestroy {
  get template(): TemplateRef<OverlayContext<{ overlayRef: OverlayRef }>> {
    return this._taHoverable;
  }

  get disabled(): boolean {
    return this._taHoverableDisabled;
  }

  @Input()
  get taHoverable(): TemplateRef<OverlayContext<{ overlayRef: OverlayRef }>> {
    return this._taHoverable;
  }
  set taHoverable(
    value: TemplateRef<OverlayContext<{ overlayRef: OverlayRef }>>
  ) {
    if (value === this._taHoverable) {
      return;
    }

    this._taHoverable = value;
  }
  private _taHoverable: TemplateRef<OverlayContext<{ overlayRef: OverlayRef }>>;

  @Input()
  get taHoverableDisabled(): boolean {
    return this._taHoverableDisabled;
  }
  set taHoverableDisabled(value: boolean) {
    value = value != null && value !== false;
    if (value === this._taHoverableDisabled) {
      return;
    }

    this._taHoverableDisabled = value;

    if (value) {
      this.hideOverlay();
    } else if (this.baseHovered) {
      setTimeout(() => {
        this.showOverlay();
      });
    }
  }
  private _taHoverableDisabled: boolean = false;

  @Input()
  hoverablePositions: PositionOption[] = [
    "bottom-left",
    "bottom-right",
    "top-left",
    "top-right"
  ];

  overlayRef?: OverlayRef;

  baseHovered: boolean;
  overlayHovered: boolean;

  @HostListener("mouseenter")
  onMouseEnter(): void {
    this.baseHovered = true;

    if (!this._taHoverable) {
      return;
    }

    this.showOverlay();
  }

  @HostListener("mouseleave")
  onMouseLeave(): void {
    this.baseHovered = false;
    if (!this._taHoverable) {
      return;
    }

    setTimeout(() => {
      if (!this.overlayHovered) {
        this.hideOverlay();
      }
    });
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.hideOverlay();
  }

  constructor(
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private overlayService: OverlayService
  ) {}

  ngOnDestroy(): void {
    this.hideOverlay();
  }

  private anchorPositionStrategy?: AnchorPositionStrategy;
  showOverlay(): void {
    if (this.overlayRef != null || this.template == null || this.disabled) {
      return;
    }

    if (this.anchorPositionStrategy == null) {
      this.anchorPositionStrategy = new AnchorPositionStrategy(
        this.elementRef.nativeElement,
        this.hoverablePositions,
        0
      );
    }

    this.overlayRef = this.overlayService.createTemplate(
      this.template,
      this.viewContainerRef,
      this.anchorPositionStrategy
    );

    let overlayElem: HTMLElement = this.overlayRef.nativeElement;
    overlayElem.addEventListener("mouseenter", this.mouseEnterHandler);
    overlayElem.addEventListener("mouseleave", this.mouseLeaveHandler);
  }

  hideOverlay(): void {
    if (this.overlayRef == null) {
      return;
    }

    let overlayElem: HTMLElement = this.overlayRef.nativeElement;
    overlayElem.removeEventListener("mouseenter", this.mouseEnterHandler);
    overlayElem.removeEventListener("mouseleave", this.mouseLeaveHandler);

    this.overlayRef.destroy();
    this.overlayRef = undefined;
  }

  mouseEnterHandler: () => void = () => {
    this.overlayHovered = true;
  };

  mouseLeaveHandler: () => void = () => {
    this.overlayHovered = false;
    if (!this.baseHovered) {
      this.hideOverlay();
    }
  };
}
